/**
 * Уніфіковані константи для всіх API запитів
 * ВАЖЛИВО: ці значення використовуються для уніфікації retry та timeout між прямими викликами та React Query
 */
export const DEFAULT_MAX_RETRIES = 1; // 1 retry = 2 спроби загалом (однакова кількість для всіх запитів)
export const DEFAULT_TIMEOUT_MS = 10000; // 10 секунд timeout (однаковий для всіх запитів)
export const DEFAULT_RETRY_DELAY_MS = 2000; // 2 секунди затримка між спробами (уніфікована для всіх запитів)

/**
 * Уніфікована функція для прямих API запитів (не через React Query)
 * 
 * ВИКОРИСТОВУЄТЬСЯ ДЛЯ:
 * - Прямих викликів API функцій (await createUserAction(), await buyRealEstate(), тощо)
 * - Запитів, які не йдуть через useQuery/useMutation
 * 
 * НЕ ВИКОРИСТОВУЄТЬСЯ ДЛЯ:
 * - React Query запитів (useQuery/useMutation) - вони мають свій retry механізм через createRetryFunction
 * 
 * ФУНКЦІОНАЛ:
 * - Повторює запит до maxRetries разів, якщо він не вдався
 * - Додає timeout для кожного запиту (10 секунд за замовчуванням)
 * - Не робить retry для серверних помилок (5xx)
 * - Експоненційна затримка між спробами
 */
export async function fetchWithRetryForDirectCalls(
  url: string,
  options: RequestInit = {},
  maxRetries: number = DEFAULT_MAX_RETRIES, // Уніфікована кількість retry
  timeoutMs: number = DEFAULT_TIMEOUT_MS // Уніфікований timeout
): Promise<Response> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      // Створюємо AbortController для timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, timeoutMs);

      try {
        // Використовуємо наш signal для timeout
        const response = await fetch(url, { ...options, signal: controller.signal });

        clearTimeout(timeoutId);

        // Якщо запит успішний (статус 200-299), повертаємо відповідь
        if (response.ok) {
          return response;
        }

        // Для серверних помилок (5xx) - не робимо retry, одразу кидаємо помилку
        if (response.status >= 500 && response.status < 600) {
          const errorText = await response.text();
          throw new Error(`Request failed: ${response.status} - ${errorText}`);
        }

        // Якщо це не остання спроба, продовжуємо
        if (attempt <= maxRetries) {
          // Затримка перед наступною спробою (уніфікована для всіх запитів)
          const delay = DEFAULT_RETRY_DELAY_MS;
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        // Якщо це остання спроба, повертаємо помилку
        const errorText = await response.text();
        throw new Error(`Request failed after ${maxRetries + 1} attempts: ${response.status} - ${errorText}`);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        // Якщо це не остання спроба і не серверна помилка, продовжуємо
        if (attempt <= maxRetries) {
          const delay = DEFAULT_RETRY_DELAY_MS;
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        
        throw fetchError;
      }
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Якщо це не остання спроба, продовжуємо
      if (attempt <= maxRetries) {
        const delay = DEFAULT_RETRY_DELAY_MS;
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Якщо це остання спроба, кидаємо помилку
      throw lastError;
    }
  }

  // Це не повинно статися, але для TypeScript
  throw lastError || new Error("Request failed after all retries");
}

/**
 * Функція для React Query запитів (useQuery/useMutation)
 * 
 * ВИКОРИСТОВУЄТЬСЯ ДЛЯ:
 * - Функцій, які використовуються в queryFn/mutationFn React Query
 * - Запитів, які йдуть через useQuery/useMutation
 * 
 * НЕ ВИКОРИСТОВУЄТЬСЯ ДЛЯ:
 * - Прямих викликів API функцій - використовуйте fetchWithRetryForDirectCalls
 * 
 * ФУНКЦІОНАЛ:
 * - Додає timeout для запиту (10 секунд за замовчуванням)
 * - НЕ робить retry - retry робить React Query через createRetryFunction
 * - Це запобігає подвійному retry (fetch + React Query)
 * 
 * УНІФІКАЦІЯ:
 * - Використовує DEFAULT_TIMEOUT_MS для однакового timeout з прямими викликами
 * - React Query retry налаштований на DEFAULT_MAX_RETRIES через createRetryFunction в main.tsx
 * - Гарантує однакову кількість спроб та timeout для всіх запитів
 */
export async function fetchWithTimeoutOnly(
  url: string,
  options: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS // Уніфікований timeout
): Promise<Response> {
  // Створюємо AbortController для timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    // Використовуємо наш signal для timeout
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
}